import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import QuestionnaireClient from "@/pages/QuestionnaireClient";
import BackOffice from "@/pages/BackOffice";
import DevisView from "@/pages/DevisView";
import ContratView from "@/pages/ContratView";
import RoadmapView from "@/pages/RoadmapView";
import NoteAuteurView from "@/pages/NoteAuteurView";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrestatairePortal from "@/pages/PrestatairePortal";
import PrestataireCalendar from "@/pages/PrestataireCalendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Safe localStorage access helper (handles cases where storage is not available)
const safeStorage = {
    getItem: (key) => {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.warn('localStorage not available:', error);
            return null;
        }
    },
    setItem: (key, value) => {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.warn('localStorage not available:', error);
        }
    },
    removeItem: (key) => {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn('localStorage not available:', error);
        }
    }
};

// Configure axios to send session token in Authorization header as fallback
axios.interceptors.request.use(
    (config) => {
        // Try to get token from localStorage (fallback if cookies don't work)
        const sessionToken = safeStorage.getItem('session_token');
        if (sessionToken) {
            // Send token in both Authorization header and X-Session-Token header
            config.headers.Authorization = `Bearer ${sessionToken}`;
            config.headers['X-Session-Token'] = sessionToken;
        }
        // Always include credentials for cookies
        config.withCredentials = true;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Export safeStorage for use in other components
export { safeStorage };

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(response.data);
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error("Logout error:", error);
        }
        // Clear session token from localStorage
        safeStorage.removeItem('session_token');
        setUser(null);
    };

    const login = () => {
        window.location.href = '/login';
    };

    useEffect(() => {
        checkAuth();
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function App() {

    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/questionnaire" element={<QuestionnaireClient />} />
                        <Route path="/devis/:id" element={<DevisView />} />
                        <Route path="/contrat/:id" element={<ContratView />} />
                        <Route path="/roadmap/:id" element={<RoadmapView />} />
                        <Route path="/notes-auteur/:id" element={<NoteAuteurView />} />
                        <Route
                            path="/prestataire-portal"
                            element={
                                <ProtectedRoute>
                                    <PrestatairePortal />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/prestataire-calendar"
                            element={
                                <ProtectedRoute>
                                    <PrestataireCalendar />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/backoffice/*"
                            element={
                                <ProtectedRoute>
                                    <BackOffice />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </div>
    );
}

export default App;
